import { useEffect, useState } from 'react';
import {
  Globe,
  Key,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Copy,
  Check,
  AlertCircle,
  Download,
  X,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, Modal } from '@centraoneplus/ui';
import {
  useIpRestrictionSettings,
  useIpWhitelist,
  useSamlConfiguration,
  useSamlMetadata,
} from '@hooks/useSecurity';
import { CreateIpWhitelistDto, CreateSamlConfigurationDto } from '@services/security.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

// ===== IP WHITELIST COMPONENT =====

function IpWhitelistSettings() {
  const { settings, isLoading: isLoadingSettings, fetchSettings, updateSettings } = useIpRestrictionSettings();
  const { whitelist, isLoading: isLoadingWhitelist, fetchWhitelist, createEntry, deleteEntry, toggleEntry } = useIpWhitelist();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newIp, setNewIp] = useState({ ipAddress: '', description: '', expiresAt: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchWhitelist();
  }, [fetchSettings, fetchWhitelist]);

  const handleToggleEnabled = async () => {
    if (!settings) return;
    try {
      await updateSettings({ isEnabled: !settings.isEnabled });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddIp = async () => {
    if (!newIp.ipAddress) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const data: CreateIpWhitelistDto = {
        ipAddress: newIp.ipAddress,
        description: newIp.description || undefined,
        expiresAt: newIp.expiresAt || undefined,
      };
      await createEntry(data);
      setShowAddModal(false);
      setNewIp({ ipAddress: '', description: '', expiresAt: '' });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this IP entry?')) return;
    try {
      await deleteEntry(id);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      await toggleEntry(id, !isActive);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">IP Restrictions</h3>
            <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
              Restrict access to your workspace based on IP addresses
            </p>
          </div>
          <button
            onClick={handleToggleEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings?.isEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                settings?.isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </Card>

      {settings?.isEnabled && (
        <>
          {/* Whitelist */}
          <Card>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Allowed IP Addresses</h3>
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add IP
              </Button>
            </div>

            {isLoadingWhitelist ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : whitelist.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Globe className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No IP addresses in whitelist</p>
                <p className="text-sm">Add IPs to restrict access</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {whitelist.map((entry) => (
                  <div key={entry.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center dark:bg-gray-800">
                        <Globe className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 font-mono dark:text-gray-100">
                          {entry.ipAddress}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {entry.description || 'No description'}
                          {entry.expiresAt && (
                            <span className="ml-2">
                              Expires: {new Date(entry.expiresAt).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={entry.isActive ? 'success' : 'default'}>
                        {entry.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <button
                        onClick={() => handleToggle(entry.id, entry.isActive)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                        title={entry.isActive ? 'Disable' : 'Enable'}
                      >
                        {entry.isActive ? (
                          <ToggleRight className="h-5 w-5 text-primary-600" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-500"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Settings */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 dark:text-gray-100">Additional Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Enforce for all users</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Apply IP restrictions to all users</p>
                </div>
                <button
                  onClick={() => updateSettings({ enforceForAll: !settings?.enforceForAll })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.enforceForAll ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      settings?.enforceForAll ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Log failed attempts</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Keep a log of blocked access attempts</p>
                </div>
                <button
                  onClick={() => updateSettings({ logFailedAttempts: !settings?.logFailedAttempts })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings?.logFailedAttempts ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      settings?.logFailedAttempts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Add IP Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add IP Address">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                IP Address *
              </label>
              <input
                type="text"
                value={newIp.ipAddress}
                onChange={(e) => setNewIp({ ...newIp, ipAddress: e.target.value })}
                placeholder="192.168.1.0/24"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Supports IPv4, IPv6, and CIDR notation
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Description
              </label>
              <input
                type="text"
                value={newIp.description}
                onChange={(e) => setNewIp({ ...newIp, description: e.target.value })}
                placeholder="Office network"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Expires At (optional)
              </label>
              <input
                type="datetime-local"
                value={newIp.expiresAt}
                onChange={(e) => setNewIp({ ...newIp, expiresAt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddIp} disabled={isSubmitting || !newIp.ipAddress}>
                {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                Add IP
              </Button>
            </div>
          </div>
        </Modal>
    </div>
  );
}

// ===== SAML SSO COMPONENT =====

function SamlSsoSettings() {
  const {
    config,
    isConfigured,
    isLoading,
    error: configError,
    fetchConfiguration,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration,
    toggleEnabled,
  } = useSamlConfiguration();
  const { metadata, fetchMetadata } = useSamlMetadata();

  const [showConfigForm, setShowConfigForm] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateSamlConfigurationDto>>({
    attributeMapping: { email: 'email', firstName: 'givenName', lastName: 'surname' },
    defaultRole: 'agent',
    autoProvisionUsers: true,
    allowIdpInitiated: true,
    wantAssertionsSigned: true,
    signatureAlgorithm: 'sha256',
    allowedClockSkew: 300,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  useEffect(() => {
    if (isConfigured) {
      fetchMetadata();
    }
  }, [isConfigured, fetchMetadata]);

  const handleSubmit = async () => {
    if (!formData.idpEntityId || !formData.idpSsoUrl || !formData.idpCertificate) {
      setError('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      if (isConfigured) {
        await updateConfiguration(formData);
      } else {
        await createConfiguration(formData as CreateSamlConfigurationDto);
      }
      setShowConfigForm(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete the SAML configuration? SSO will be disabled.')) return;
    try {
      await deleteConfiguration();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggle = async () => {
    if (!config) return;
    try {
      await toggleEnabled(!config.isEnabled);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDownloadMetadata = () => {
    if (!metadata?.metadataXml) return;
    const blob = new Blob([metadata.metadataXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'saml-metadata.xml';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(error || configError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error || configError}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {!isConfigured ? (
        /* Setup SAML */
        <Card className="p-6">
          <div className="text-center py-8">
            <Key className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">
              Set Up SAML Single Sign-On
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto dark:text-gray-400">
              Allow users to sign in using your organization&apos;s identity provider (IdP) like Okta, Azure AD, or Google Workspace.
            </p>
            <Button onClick={() => setShowConfigForm(true)}>
              <Key className="h-4 w-4 mr-2" />
              Configure SAML SSO
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {/* SSO Status */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                  config?.isEnabled ? 'bg-green-100' : 'bg-gray-100 dark:bg-gray-800'
                }`}>
                  <Key className={`h-6 w-6 ${
                    config?.isEnabled ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'
                  }`} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">SAML Single Sign-On</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {config?.isEnabled ? 'SSO is enabled for your organization' : 'SSO is configured but not enabled'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={config?.isEnabled ? 'success' : 'default'}>
                  {config?.isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <button
                  onClick={handleToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config?.isEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      config?.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </Card>

          {/* SP Metadata */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 dark:text-gray-100">Service Provider Details</h3>
            <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
              Provide these values to your Identity Provider when setting up the SAML application.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">Entity ID (Issuer)</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate dark:bg-gray-900 dark:text-gray-200">
                    {metadata?.entityId || config?.spEntityId}
                  </code>
                  <button
                    onClick={() => handleCopy(metadata?.entityId || config?.spEntityId || '', 'entityId')}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                  >
                    {copied === 'entityId' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">ACS URL (Reply URL)</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-700 truncate dark:bg-gray-900 dark:text-gray-200">
                    {metadata?.acsUrl || config?.spAcsUrl}
                  </code>
                  <button
                    onClick={() => handleCopy(metadata?.acsUrl || config?.spAcsUrl || '', 'acsUrl')}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                  >
                    {copied === 'acsUrl' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" size="sm" onClick={handleDownloadMetadata}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Metadata XML
                </Button>
              </div>
            </div>
          </Card>

          {/* IdP Configuration */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Identity Provider Configuration</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowConfigForm(true)}>
                Edit
              </Button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">IdP Entity ID</label>
                <p className="text-sm text-gray-900 font-mono dark:text-gray-100">{config?.idpEntityId}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">SSO URL</label>
                <p className="text-sm text-gray-900 font-mono dark:text-gray-100">{config?.idpSsoUrl}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Certificate</label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {config?.hasCertificate ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <Check className="h-4 w-4" /> Certificate configured
                    </span>
                  ) : (
                    <span className="text-red-600">No certificate configured</span>
                  )}
                </p>
              </div>
            </div>
          </Card>

          {/* Settings */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4 dark:text-gray-100">SSO Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Auto-provision users</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Automatically create users on first SSO login</p>
                </div>
                <Badge variant={config?.autoProvisionUsers ? 'success' : 'default'}>
                  {config?.autoProvisionUsers ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Default role</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Role assigned to new SSO users</p>
                </div>
                <Badge variant="info">{config?.defaultRole}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Allow IdP-initiated SSO</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Allow login initiated from the IdP</p>
                </div>
                <Badge variant={config?.allowIdpInitiated ? 'success' : 'default'}>
                  {config?.allowIdpInitiated ? 'Allowed' : 'Not allowed'}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card className="p-6 border-red-200">
            <h3 className="text-sm font-medium text-red-600 mb-2">Danger Zone</h3>
            <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
              Deleting the SAML configuration will disable SSO for all users.
            </p>
            <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-300 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete SAML Configuration
            </Button>
          </Card>
        </>
      )}

      {/* Configuration Form Modal */}
      <Modal
        isOpen={showConfigForm}
        onClose={() => setShowConfigForm(false)}
        title={isConfigured ? 'Edit SAML Configuration' : 'Configure SAML SSO'}
        size="lg"
      >
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                IdP Entity ID (Issuer) *
              </label>
              <input
                type="text"
                value={formData.idpEntityId || ''}
                onChange={(e) => setFormData({ ...formData, idpEntityId: e.target.value })}
                placeholder="https://idp.example.com/saml/metadata"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                IdP SSO URL *
              </label>
              <input
                type="url"
                value={formData.idpSsoUrl || ''}
                onChange={(e) => setFormData({ ...formData, idpSsoUrl: e.target.value })}
                placeholder="https://idp.example.com/saml/sso"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                IdP Single Logout URL (optional)
              </label>
              <input
                type="url"
                value={formData.idpSloUrl || ''}
                onChange={(e) => setFormData({ ...formData, idpSloUrl: e.target.value })}
                placeholder="https://idp.example.com/saml/slo"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                IdP X.509 Certificate (PEM format) *
              </label>
              <textarea
                value={formData.idpCertificate || ''}
                onChange={(e) => setFormData({ ...formData, idpCertificate: e.target.value })}
                placeholder="-----BEGIN CERTIFICATE-----&#10;MIICpDCCAYw...&#10;-----END CERTIFICATE-----"
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
              />
            </div>

            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-900 mb-3 dark:text-gray-100">User Provisioning</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-700 dark:text-gray-200">Auto-provision new users</label>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, autoProvisionUsers: !formData.autoProvisionUsers })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.autoProvisionUsers ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        formData.autoProvisionUsers ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1 dark:text-gray-200">Default role</label>
                  <ThemedSelect
                    value={formData.defaultRole || 'agent'}
                    onChange={(e) => setFormData({ ...formData, defaultRole: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700"
                  >
                    <option value="agent">Agent</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                  </ThemedSelect>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowConfigForm(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                {isConfigured ? 'Update Configuration' : 'Save Configuration'}
              </Button>
            </div>
          </div>
        </Modal>
    </div>
  );
}

// ===== MAIN COMPONENT =====

export default function SecuritySettingsPage() {
  const [activeTab, setActiveTab] = useState<'ip' | 'sso'>('ip');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage IP restrictions and single sign-on</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('ip')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'ip'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Globe className="h-4 w-4" />
            IP Restrictions
          </button>
          <button
            onClick={() => setActiveTab('sso')}
            className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'sso'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Key className="h-4 w-4" />
            Single Sign-On (SAML)
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'ip' && <IpWhitelistSettings />}
      {activeTab === 'sso' && <SamlSsoSettings />}
    </div>
  );
}
